import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';

interface CobranzaOption {
  title: string;
  description: string;
  icon: string;
  route?: string;
  action?: string; // Para cards que abren modal/acción en vez de navegar
  requiredRoles: string[];
  color: string; // Tailwind color class
  rbacPermissions?: string[];
  prodOnly?: boolean;
}

@Component({
  selector: 'app-cobranza-dashboard',
  templateUrl: './cobranza-dashboard.component.html',
  styleUrls: []
})
export class CobranzaDashboardComponent implements OnInit {
  options: CobranzaOption[] = [
    {
      title: 'Transacciones 3DS',
      description: 'Auditoría y gestión de pagos con tarjeta internacional (CROEM 3DS)',
      icon: 'fas fa-credit-card',
      route: '/console/transacciones-3ds',
      requiredRoles: ['OWNER', 'FINANCIAL', 'ACCOUNTING_ANALYST'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Afiliaciones Bancarias',
      description: 'Administra los archivos de afiliación bancaria para débitos automáticos en los bancos afiliados',
      icon: 'fas fa-handshake',
      route: '/console/dashboard-direct-debit-affiliation',
      requiredRoles: ['OWNER', 'FINANCIAL'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.afiliaciones.read']
    },
    {
      title: 'Domiciliaciones Bancarias',
      description: 'Administra los archivos de cobranza a través de débito automático en los bancos afiliados',
      icon: 'fa fa-credit-card',
      route: '/console/dashboard-direct-debit',
      requiredRoles: ['OWNER', 'FINANCIAL'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.domiciliaciones.read']
    },
    {
      title: 'Gestión de Cuotas',
      description: 'Monitorea las cuotas financiadas por estatus y vencimientos',
      icon: 'fas fa-calendar-alt',
      route: '/console/installments-console',
      requiredRoles: ['OWNER', 'ANALIST'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.cuotas.read']
    },
    {
      title: 'Notificaciones por Canales',
      description: 'Configura y envía notificaciones masivas a través de diferentes canales',
      icon: 'fas fa-bell',
      route: '/console/notificacion-xlsx',
      requiredRoles: ['OWNER'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.notificaciones.read']
    },
    {
      title: 'Consultas BDV',
      description: 'Consulta movimientos, saldos y notificaciones del Banco de Venezuela',
      icon: 'fas fa-search-dollar',
      route: '/console/bdv-consultas',
      requiredRoles: ['OWNER'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.bdv_consultas.read'],
      prodOnly: false
    },
    {
      title: 'Banco Plaza - Débito Inmediato',
      description: 'Gestión de transacciones y configuración de débito inmediato con Banco Plaza',
      icon: 'fas fa-university',
      route: '/console/banco-plaza',
      requiredRoles: ['OWNER', 'ANALIST'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.banco_plaza.read']
    },
    {
      title: 'Pagos en Caja',
      description: 'Gestión de pagos en caja',
      icon: 'fas fa-cash-register',
      route: '/console/caja',
      requiredRoles: ['OWNER', 'ANALIST'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.pagos_caja.read']
    },
    {
      title: 'Distribución de Pagos',
      description: 'Consulta la distribución de pagos de un cliente por cédula',
      icon: 'fas fa-chart-pie',
      action: 'open_distri_search',
      requiredRoles: ['OWNER', 'ANALIST'],
      color: 'bg-blue-100 text-blue-600',
      rbacPermissions: ['cobranza.distribucion_pagos.read']
    },
    {
      title: 'Cartera y Mora',
      description: 'Centro de operaciones de cartera: morosidad, indicadores NPL, comportamiento de pago, gestión de cobro',
      icon: 'fas fa-chart-line',
      route: '/console/cartera',
      requiredRoles: ['OWNER', 'ADMIN', 'ANALIST'],
      rbacPermissions: ['cobranza.cartera_mora.read'],
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  userRoles: string[] = [];

  // Modal de búsqueda por cédula para "Distribución de Pagos"
  showDistriSearchModal = false;
  distriSearchTypeIdentity: 'V' | 'E' | 'J' = 'V';
  distriSearchIdentity = '';
  distriSearchLoading = false;
  distriSearchError = '';

  constructor(
    public _auth: AuthService,
    private _user: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.userRoles = Array.isArray(this._auth.sessionUser?.profile) ? this._auth.sessionUser.profile : [];
  }

  hasPermission(option: CobranzaOption): boolean {
    if (option.prodOnly && !environment.production) return false;
    if (this._auth.hasRbac$.getValue()) {
      if (option.rbacPermissions && option.rbacPermissions.length > 0) {
        return this._auth.hasAnyPermission(option.rbacPermissions);
      }
      return this._auth.hasModuleAccess('cobranza');
    }
    if (!option.requiredRoles || option.requiredRoles.length === 0) return true;
    return option.requiredRoles.some(role => this.userRoles.includes(role));
  }

  // Handler único de click para cards: navega si tiene route, o dispara action
  handleOptionClick(option: CobranzaOption): void {
    if (option.route) {
      this.router.navigate([option.route]);
      return;
    }
    if (option.action === 'open_distri_search') {
      this.openDistriSearchModal();
    }
  }

  openDistriSearchModal(): void {
    this.distriSearchTypeIdentity = 'V';
    this.distriSearchIdentity = '';
    this.distriSearchError = '';
    this.distriSearchLoading = false;
    this.showDistriSearchModal = true;
  }

  closeDistriSearchModal(): void {
    this.showDistriSearchModal = false;
  }

  async searchDistriByIdentity(): Promise<void> {
    const identity = (this.distriSearchIdentity || '').trim();
    if (!identity) {
      this.distriSearchError = 'Ingrese la cédula';
      return;
    }
    this.distriSearchError = '';
    this.distriSearchLoading = true;
    try {
      const body = {
        type_identity: 'CI',
        code_identity: this.distriSearchTypeIdentity,
        identity: identity
      };
      const resp: any = await this._user.getPersonByIdentifier(body);
      const code = resp?.resp?.person?.code;
      if (!code) {
        this.distriSearchError = 'Cliente no existe';
        this.distriSearchLoading = false;
        return;
      }
      this.closeDistriSearchModal();
      this.router.navigate(['/console/person/distri/', code]);
    } catch (error: any) {
      this.distriSearchError = error?.error?.error?.msg || 'Error al buscar el cliente';
      this.distriSearchLoading = false;
    }
  }
}
